import { useState, useEffect, useCallback } from 'react';
import { APP_VERSION } from '../constants/version';

/**
 * Hook to check for application version updates
 * Checks version every 10 minutes or on window focus
 */
export const useVersionCheck = () => {
  const [isNewVersionAvailable, setIsNewVersionAvailable] = useState(false);

  const checkVersion = useCallback(async () => {
    try {
      // Fetch version from server with cache busting
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        // If version.json doesn't exist, assume we're up to date
        console.debug('[VersionCheck] version.json not found, assuming up to date');
        return;
      }

      // Check content type to ensure we got JSON, not HTML (404 fallback)
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.debug('[VersionCheck] Response is not JSON, got:', contentType);
        return;
      }

      const data = await response.json();
      const remoteVersion = data.version;

      console.debug('[VersionCheck] Current:', APP_VERSION, 'Remote:', remoteVersion);

      // Compare versions - if remote version differs, update is available
      if (remoteVersion && remoteVersion !== APP_VERSION) {
        console.log('[VersionCheck] ✅ New version available:', remoteVersion);
        setIsNewVersionAvailable(true);
      }
    } catch (error) {
      // Silently fail - don't interrupt user experience
      console.debug('[VersionCheck] Failed to check version:', error);
    }
  }, []);

  useEffect(() => {
    // Initial check
    checkVersion();

    // Check every 10 minutes
    const interval = setInterval(
      () => {
        checkVersion();
      },
      10 * 60 * 1000
    ); // 10 minutes

    // Check on window focus
    const handleFocus = () => {
      checkVersion();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [checkVersion]);

  return { isNewVersionAvailable };
};
